const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Format a sorted list of weekday numbers (0=Sun…6=Sat) as "Mon, Wed, Fri". */
export function formatRecurrenceDays(days: number[]): string {
  return [...days].sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ');
}

/**
 * Event time utilities: times are stored as UTC HH:MM strings on the server.
 * These helpers convert between UTC storage and the viewer's local timezone for display,
 * and from local input to UTC for submission.
 */

/** Convert a local-timezone HH:MM time + YYYY-MM-DD date → UTC HH:MM for API storage. */
export function localTimeToUtc(date: string, localTime: string): string {
  return new Date(`${date}T${localTime}:00`).toISOString().slice(11, 16);
}

/** Convert a stored UTC HH:MM time + YYYY-MM-DD date → HH:MM in the viewer's local timezone. */
export function utcTimeToLocal(date: string, utcTime: string): string {
  return new Date(`${date}T${utcTime}:00Z`).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Format an API timestamp in the viewer's local timezone. */
export function formatLocalTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format a start–end time range for display, converting from UTC to local timezone.
 * Handles past-midnight end times (e.g. start 21:00 UTC, end 02:00 UTC next day).
 */
export function formatEventTime(
  eventDate?: string,
  startUtc?: string,
  endUtc?: string,
): string {
  if (!startUtc || !eventDate) return startUtc ?? '';

  const localStart = utcTimeToLocal(eventDate, startUtc);
  if (!endUtc) return localStart;

  // If end UTC time is earlier or equal to start UTC, the event crosses midnight
  let endDate = eventDate;
  if (endUtc <= startUtc) {
    const d = new Date(`${eventDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    endDate = d.toISOString().slice(0, 10);
  }

  return `${localStart} – ${utcTimeToLocal(endDate, endUtc)}`;
}
